import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./exam-questions.component.scoped.scss";
import { Box, Typography } from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import AppButtonComponent from "../../../../../components/form/app-button";
import AppCheckboxComponent from "../../../../../components/form/app-checkbox";
import { services } from "../../../../../services";
import { showPopupToastMsg, showToastMsg } from "../../../../../helpers";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { educationRoutes } from "../../../types/education.routes";
import { history } from "react-router-guard";
import { ConfirmFinishExamDialog } from "../../../modals/confirm-finish-exam/confirm-finish-exam.dialog";

interface ExamAnswer {
  id: number | string;
  text: string;
}

interface ExamQuestion {
  id: number | string;
  question: string;
  answers: ExamAnswer[];
}

interface ExamQuestionsProps {
  loading: boolean;
  questions?: ExamQuestion[];
  examId: string;
  checkpoint: any;
  onExamComplete?: (userResult: any) => void;
}

const ExamQuestionsComponent = ({
  loading = true,
  questions = [],
  examId,
  checkpoint,
  onExamComplete,
}: ExamQuestionsProps) => {
  const { t } = useTranslation();
  const [checkpointDetails, setCheckpointDetails] = useState<any>({});
  useEffect(() => {
    setCheckpointDetails(checkpoint);
  }, [checkpoint]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<
    Record<string, string[]>
  >({});
  const [
    showConfirmFinishExamDialog,
    setShowConfirmFinishExamDialog,
  ] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const examQuestions = useMemo(
    () => (Array.isArray(questions) ? questions : []),
    [questions],
  );

  const totalQuestions = examQuestions.length;
  const isFirstQuestion = currentIndex === 0;
  const isLastQuestion =
    totalQuestions > 0 && currentIndex === totalQuestions - 1;
  const currentQuestion = examQuestions[currentIndex];

  const answeredCount = useMemo(
    () =>
      examQuestions.filter(
        (question) => (selectedAnswers[String(question.id)]?.length ?? 0) > 0,
      ).length,
    [examQuestions, selectedAnswers],
  );

  const progressPercent =
    totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  const currentQuestionAnswered = currentQuestion
    ? (selectedAnswers[String(currentQuestion.id)]?.length ?? 0) > 0
    : false;

  const questionsKey = useMemo(() => examQuestions.map((q) => q.id).join(","), [
    examQuestions,
  ]);

  useEffect(() => {
    setCurrentIndex(0);
    setSelectedAnswers({});
  }, [examId, questionsKey]);

  const toggleAnswer = useCallback(
    (questionId: number | string, answerId: string) => {
      const key = String(questionId);
      setSelectedAnswers((prev) => {
        const current = prev[key] ?? [];
        const next = current.includes(answerId)
          ? current.filter((id) => id !== answerId)
          : [...current, answerId];

        return { ...prev, [key]: next };
      });
    },
    [],
  );

  const handleBack = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    if (!currentQuestionAnswered) return;
    setCurrentIndex((prev) => Math.min(totalQuestions - 1, prev + 1));
  }, [currentQuestionAnswered, totalQuestions]);

  const buildSubmitPayload = useCallback(() => {
    return examQuestions.map((question) => ({
      questionId: question.id,
      answerIds: (selectedAnswers[String(question.id)] ?? []).map(Number),
    }));
  }, [examQuestions, selectedAnswers]);

  const handleSubmit = useCallback(async () => {
    setSubmitting(true);
    try {
      const response = await services.courses.submitCheckpointExam(examId, {
        answers: buildSubmitPayload(),
      });

      if (response?.message === "SUCCESS") {
        showToastMsg(
          "success",
          t("EDUCATION_PAGE.EXAM_SUBMITTED_SUCCESSFULLY"),
        );
        onExamComplete?.(response);
      } else {
        showPopupToastMsg(
          "error",
          response?.message || t("SOME_THING_WENT_WRONG"),
        );
      }
    } catch (error) {
      console.error("Error submitting exam:", error);
      showPopupToastMsg("error", t("SOME_THING_WENT_WRONG"));
    } finally {
      setSubmitting(false);
    }
  }, [buildSubmitPayload, examId, onExamComplete, t]);

  const confirmFinishExam = () => {
    if (!currentQuestionAnswered || answeredCount !== totalQuestions) {
      showToastMsg(
        "error",
        t("EDUCATION_PAGE.PLEASE_ANSWER_ALL_EXAM_QUESTIONS"),
      );
      return;
    }
    setShowConfirmFinishExamDialog(true);
  };

  const handleConfirmFinishExam = (value: boolean) => {
    setShowConfirmFinishExamDialog(false);
    if (value) {
      handleSubmit();
    }
  };

  return (
    <Box className="exam__content">
      <Box className="global__box__content p-0 mb-5 exam-questions">
        <Box
          className="global__box__content__header"
          style={{ padding: "15px 1.9rem" }}
        >
          {loading ? (
            <Skeleton variant="text" width={200} height={24} />
          ) : (
            <Box
              className="d-flex align-items-center"
              style={{ gap: "1.9rem" }}
            >
              <Typography variant="h2" className="font-weight-bold">
                {t("EDUCATION_PAGE.QUESTIONS")}
              </Typography>
              <Typography variant="caption">
                {totalQuestions} {t("TOTAL")}
              </Typography>
            </Box>
          )}
        </Box>

        <Box
          className="global__box__content__body"
          style={{ padding: "1.9rem" }}
        >
          {loading ? (
            <>
              <Skeleton
                variant="rect"
                width="100%"
                height={6}
                className="mb-2"
              />
              <Skeleton
                variant="text"
                width={160}
                height={16}
                className="mb-4"
              />
              <Skeleton
                variant="text"
                width="80%"
                height={24}
                className="mb-3"
              />
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="55%" height={20} />
            </>
          ) : totalQuestions === 0 ? (
            <Typography variant="body1" color="textSecondary">
              {t("EDUCATION_PAGE.NO_EXAM_QUESTIONS")}
            </Typography>
          ) : (
            <>
              <Box className="track__bar">
                <span className="progress__track">
                  <span
                    className="progress__fill"
                    style={{ width: `${progressPercent}%` }}
                  />
                </span>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  className="text-center"
                >
                  {t("EDUCATION_PAGE.EXAM_QUESTIONS_PROGRESS", {
                    answered: answeredCount,
                    total: totalQuestions,
                  })}
                </Typography>
              </Box>

              <Box
                className="global__box__content m-auto"
                style={{ width: "65%" }}
              >
                <Typography
                  variant="h3"
                  className="font-weight-bold mb-4 text-center"
                >
                  {t("QUESTION")} {currentIndex + 1} {t("OF")} {totalQuestions}
                </Typography>
                <Typography variant="body1" className="mb-4">
                  {currentQuestion.question}
                </Typography>

                <Typography variant="h4" className="mb-4 font-weight-bold">
                  {t("EDUCATION_PAGE.ANSWERS")}
                </Typography>

                {currentQuestion.answers?.length > 0 ? (
                  <Box
                    className="exam-answers-list"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                  >
                    {currentQuestion.answers.map((answer) => {
                      const answerId = String(answer.id);
                      const selected =
                        selectedAnswers[String(currentQuestion.id)] ?? [];

                      return (
                        <AppCheckboxComponent
                          key={answerId}
                          label={answer.text}
                          checked={selected.includes(answerId)}
                          onChange={() =>
                            toggleAnswer(currentQuestion.id, answerId)
                          }
                          disabled={submitting}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary">
                    {t("EDUCATION_PAGE.NO_ANSWERS_AVAILABLE")}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box className="question__navigation mb-5">
        {loading ? (
          <Skeleton variant="text" width={200} height={24} />
        ) : (
          <Typography variant="body1" className="font-weight-bold">
            {t("EDUCATION_PAGE.MOVE_ON_TO_THE_NEXT_QUESTION")}
          </Typography>
        )}
        <Box className="actions">
          {loading ? (
            <Skeleton variant="rect" width={80} height={36} />
          ) : (
            <AppButtonComponent
              variant="contained"
              color="primary"
              onClick={handleBack}
              disabled={submitting || isFirstQuestion}
            >
              {t("BACK")}
            </AppButtonComponent>
          )}

          {loading ? (
            <Skeleton variant="rect" width={80} height={36} />
          ) : (
            <>
              {isLastQuestion ? (
                <AppButtonComponent
                  variant="contained"
                  color="primary"
                  onClick={confirmFinishExam}
                  disabled={!currentQuestionAnswered || submitting}
                >
                  {t("SUBMIT")}
                </AppButtonComponent>
              ) : (
                <AppButtonComponent
                  variant="contained"
                  color="primary"
                  onClick={handleNext}
                  disabled={!currentQuestionAnswered || submitting}
                >
                  {t("NEXT")}
                </AppButtonComponent>
              )}
            </>
          )}
          {showConfirmFinishExamDialog && (
            <ConfirmFinishExamDialog
              show={showConfirmFinishExamDialog}
              onClose={handleConfirmFinishExam}
            />
          )}
        </Box>
      </Box>

      {loading ? (
        <Skeleton variant="text" width={500} height={64} />
      ) : (
        <Box
          onClick={() =>
            history.push(educationRoutes.course(checkpointDetails?.course?.id))
          }
          className="global__box__content d-flex align-items-center w-50"
          style={{ padding: "15px 2rem", gap: "1rem", cursor: "pointer" }}
        >
          <ArrowBackIosIcon style={{ fontSize: "24px" }} />
          <Box className="d-flex flex-column">
            <Typography
              variant="h4"
              color="textPrimary"
              className="font-weight-bold"
            >
              {t("BACK")}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {checkpointDetails?.course?.title}
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ExamQuestionsComponent;
